import { PersonIcon } from '@navikt/aksel-icons';
import { ContextDataType, useContextGetData, useContextSaveData } from 'appData/PlanleggerDataContext';
import { useStepData } from 'appData/useStepData';
import { BlueRadioGroup } from 'components/form-wrappers/BlueRadioGroup';
import { PlanleggerStepPage } from 'components/page/PlanleggerStepPage';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { HvemPlanlegger, HvemPlanleggerType } from 'types/HvemPlanlegger';
import { formatError } from 'utils/customErrorFormatter';

import { BodyLong, BodyShort, Link, Radio, Spacer, VStack } from '@navikt/ds-react';

import { links } from '@navikt/fp-constants';
import { RhfForm, RhfTextField, StepButtonsHookForm } from '@navikt/fp-form-hooks';
import { BluePanel, Infobox } from '@navikt/fp-ui';
import { useScrollBehaviour } from '@navikt/fp-utils';
import { isRequired } from '@navikt/fp-validation';

import { usePlanleggerNavigator } from '../../app-data/usePlanleggerNavigator';

const skalViseMorNavnfelt = (type: HvemPlanleggerType) => {
    return type === HvemPlanleggerType.MOR_OG_FAR || type === HvemPlanleggerType.MOR_OG_MEDMOR;
};

const skalViseFarNavnfelt = (type: HvemPlanleggerType) => {
    return type === HvemPlanleggerType.MOR_OG_FAR || type === HvemPlanleggerType.FAR_OG_FAR;
};

export const HvemPlanleggerSteg = () => {
    const intl = useIntl();
    const navigator = usePlanleggerNavigator();
    const stepConfig = useStepData();

    const hvemPlanlegger = useContextGetData(ContextDataType.HVEM_PLANLEGGER);
    const oppdaterHvemPlanlegger = useContextSaveData(ContextDataType.HVEM_PLANLEGGER);
    const oppdaterOmBarnet = useContextSaveData(ContextDataType.OM_BARNET);
    const oppdaterArbeidssituasjon = useContextSaveData(ContextDataType.ARBEIDSSITUASJON);
    const oppdaterHvorLangPeriode = useContextSaveData(ContextDataType.HVOR_LANG_PERIODE);
    const oppdaterFordeling = useContextSaveData(ContextDataType.FORDELING);
    const oppdaterHvorMye = useContextSaveData(ContextDataType.HVOR_MYE);
    const oppdaterUttaksplan = useContextSaveData(ContextDataType.UTTAKSPLAN);

    const lagre = (formValues: HvemPlanlegger) => {
        oppdaterHvemPlanlegger(formValues);
        if (hvemPlanlegger && hvemPlanlegger.type !== formValues.type) {
            oppdaterOmBarnet(undefined);
            oppdaterArbeidssituasjon(undefined);
            oppdaterHvorLangPeriode(undefined);
            oppdaterFordeling(undefined);
            oppdaterHvorMye(undefined);
            oppdaterUttaksplan(undefined);
        }

        navigator.goToNextDefaultStep();
    };

    const formMethods = useForm<HvemPlanlegger>({ defaultValues: hvemPlanlegger, shouldUnregister: true });

    const type = formMethods.watch('type');

    const erHvemPlanleggerIkkeOppgittFraFør = hvemPlanlegger === undefined;

    const { ref, scrollToBottom } = useScrollBehaviour();

    return (
        <PlanleggerStepPage ref={ref} steps={stepConfig} goToStep={navigator.goToNextStep}>
            <RhfForm formMethods={formMethods} onSubmit={lagre} shouldUseFlexbox>
                <VStack gap="space-40" style={{ flex: 1 }}>
                    <VStack gap="space-32">
                        <BodyShort>
                            <FormattedMessage id="HvemPlanleggerSteg.HarValgfrieFelt" />
                        </BodyShort>
                        <BlueRadioGroup
                            name="type"
                            control={formMethods.control}
                            label={intl.formatMessage({
                                id: 'HvemPlanleggerSteg.HvemPlanlegger',
                            })}
                            validate={[
                                isRequired(
                                    intl.formatMessage({
                                        id: 'HvemPlanleggerSteg.HvemPlanlegger.Required',
                                    }),
                                ),
                            ]}
                            onChange={scrollToBottom}
                        >
                            <Radio value={HvemPlanleggerType.MOR_OG_FAR} autoFocus>
                                <FormattedMessage id="HvemPlanleggerSteg.MorOgFar" />
                            </Radio>
                            <Radio value={HvemPlanleggerType.MOR_OG_MEDMOR}>
                                <FormattedMessage id="HvemPlanleggerSteg.MorOgMedmor" />
                            </Radio>
                            <Radio value={HvemPlanleggerType.FAR_OG_FAR}>
                                <FormattedMessage id="HvemPlanleggerSteg.FarOgFar" />
                            </Radio>
                            <Radio value={HvemPlanleggerType.MOR}>
                                <FormattedMessage id="HvemPlanleggerSteg.BareMor" />
                            </Radio>
                            <Radio value={HvemPlanleggerType.FAR}>
                                <FormattedMessage id="HvemPlanleggerSteg.BareFar" />
                            </Radio>
                        </BlueRadioGroup>
                        {(type === HvemPlanleggerType.MOR || type === HvemPlanleggerType.FAR) && (
                            <Infobox
                                header={<FormattedMessage id="HvemPlanleggerSteg.Infoboks" />}
                                icon={
                                    <PersonIcon
                                        height={24}
                                        width={24}
                                        color="var(--ax-bg-success-strong)"
                                        fontSize="1.5rem"
                                        aria-hidden
                                    />
                                }
                                shouldFadeIn
                                color="green"
                            >
                                <VStack gap="space-8">
                                    <BodyLong>
                                        <FormattedMessage
                                            id="HvemPlanleggerSteg.Infoboks.Tekst"
                                            values={{
                                                a: (msg) => (
                                                    <Link
                                                        href={links.hvorLenge}
                                                        rel="noreferrer"
                                                        target="_blank"
                                                        inlineText
                                                    >
                                                        {msg}
                                                    </Link>
                                                ),
                                            }}
                                        />
                                    </BodyLong>
                                </VStack>
                            </Infobox>
                        )}
                        {type && type !== HvemPlanleggerType.MOR && type !== HvemPlanleggerType.FAR && (
                            <BluePanel isDarkBlue={erHvemPlanleggerIkkeOppgittFraFør} shouldFadeIn>
                                <VStack gap="space-40">
                                    {skalViseMorNavnfelt(type) && (
                                        <RhfTextField
                                            name="navnPåMor"
                                            control={formMethods.control}
                                            label={intl.formatMessage({ id: 'HvemPlanleggerSteg.Mor' })}
                                            customErrorFormatter={formatError}
                                        />
                                    )}
                                    {skalViseFarNavnfelt(type) && (
                                        <RhfTextField
                                            name="navnPåFar"
                                            control={formMethods.control}
                                            label={intl.formatMessage({ id: 'HvemPlanleggerSteg.Far' })}
                                            customErrorFormatter={formatError}
                                        />
                                    )}
                                    {type === HvemPlanleggerType.MOR_OG_MEDMOR && (
                                        <RhfTextField
                                            name="navnPåMedmor"
                                            control={formMethods.control}
                                            label={intl.formatMessage({ id: 'HvemPlanleggerSteg.Medmor' })}
                                            customErrorFormatter={formatError}
                                        />
                                    )}
                                    {type === HvemPlanleggerType.FAR_OG_FAR && (
                                        <RhfTextField
                                            name="navnPåMedfar"
                                            control={formMethods.control}
                                            label={intl.formatMessage({ id: 'HvemPlanleggerSteg.Far' })}
                                            customErrorFormatter={formatError}
                                        />
                                    )}
                                </VStack>
                            </BluePanel>
                        )}
                    </VStack>
                    <Spacer />
                    <StepButtonsHookForm goToPreviousStep={navigator.goToPreviousDefaultStep} useSimplifiedTexts />
                </VStack>
            </RhfForm>
        </PlanleggerStepPage>
    );
};
